import React, {useMemo} from 'react';
import {connect} from 'react-redux';
import SMCPage from '../../../components/core/pageComponents/SMCPage';
import {useResultsTableDimensions, useToggle, useBoolean} from '../../../utils/hooks';
import SMCTable from '../../../components/core/SMCTable/SMCTable';
import {useSystemUserResults} from './systemUserResultsHelper';
import SystemUserFilterPane from '../../Filters/SystemUserFilterPane';
import SystemUserWizard from '../../Wizards/SystemUserWizard';
import {isSystemUserSelector, systemUserPermissionsSelector, isUserAdminSelector, isAdminReadOnlySelector} from '../../../store/selectors/user.selectors';
import {helpContextPageUrl} from '../../../conf/Routes';
import SMCTableActions from '../../../components/core/SMCTableActions/SMCTableActions';
import {SystemUserFilterSchema} from '../../../api/DBSchema/SystemUser';

function SystemUserResults({
    isUserAdmin,
    isAdminReadOnly,
    isSystemUser,
    systemUserPermissions
}) {
    const [tableWidth, tableHeight] = useResultsTableDimensions();
    const [isFilterPaneOpen, toggleFilterPane] = useToggle(false);
    const [isNewObjectWindowOpen, openNewObjectWindow, closeNewObjectWindow] = useBoolean(false);

    const {
        tableContent,
        isFetching,
        isPageLoaded,
        filterObject,
        setFilterObject,
        freeTextSearch,
        fetchNextPage,
        isLastPage,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        rowIdSelector,
        selectedObjects,
        setSelectedObjects,
        columnsWidths,
        setColumnsWidths,
        newObjectCallback,
        reloadButtonOnClick,
        deleteButtonOnClick
    } = useSystemUserResults();

    const canDelete = useMemo(() => {
        // System users can't delete
        if (isSystemUser) {
            return false;
        }
        if (isUserAdmin) {
            return !isAdminReadOnly;
        }
        if (systemUserPermissions) {
            return systemUserPermissions.delete;
        }
        return false;
    }, [isSystemUser, isUserAdmin, systemUserPermissions, isAdminReadOnly]);
    
    const canCreate = useMemo(() => {
        // System users can't create
        if (isSystemUser) {
            return false;
        }
        if (isUserAdmin) {
            return !isAdminReadOnly;
        }
        if (systemUserPermissions) {
            return systemUserPermissions.create;
        }
        return false;
    }, [isSystemUser, isUserAdmin, systemUserPermissions, isAdminReadOnly]);

    return (
    <SMCPage loading={!isPageLoaded} helpId={helpContextPageUrl.showingUsers}>            
            <div className="DrawResultsDiv">
            <SMCTableActions
                        openNewObjectWindow={openNewObjectWindow}
                        reloadButtonOnClick={reloadButtonOnClick}
                        deleteButtonOnClick={deleteButtonOnClick}
                        toggleFilterPane={toggleFilterPane}
                        canCreate={canCreate}
                        canDelete={canDelete}
                        isFetching={isFetching} 
                        tableContent={tableContent}
                        isFilterPaneOpen={isFilterPaneOpen}
                        initialSearch={filterObject}
                        onSearchChange={setFilterObject}
                        searchAttribute={SystemUserFilterSchema.freeTextSearch}
                        selectedObjects={selectedObjects}
                /> 
                <SMCTable
                    columns={columns}
                    columnsState={columnsState}
                    setColumnsState={setColumnsState}
                    setDefaultColumnsState={setDefaultColumnsState}
                    content={tableContent}
                    loading={isFetching}
                    selectedObjects={selectedObjects}
                    setSelectedObjects={setSelectedObjects}
                    hasCheckAll={true}
                    defaultColumnsWidths={columnsWidths}
                    sortBasis={sortBasis}
                    sortDirection={sortDirection}
                    setSort={setSort}
                    onEndResizingColumns={setColumnsWidths}
                    onRequestLoadPage={fetchNextPage}
                    isLastPage={isLastPage}
                    width={tableWidth}
                    height={tableHeight}
                    rowIdSelector={rowIdSelector}
                    isFilterOpen={isFilterPaneOpen}
                    filterElem={
                        <SystemUserFilterPane
                            initialFilter={filterObject}
                            onFilterChange={setFilterObject}
                            open={isFilterPaneOpen}
                        />
                    }
                    textToHighlight={freeTextSearch}
                    showEditIcon={false}
                />
                {/* Floating windows */}
                <SystemUserWizard
                    isOpen={isNewObjectWindowOpen}
                    onRequestClose={closeNewObjectWindow}
                    onSuccess={newObjectCallback}
                />
            </div>
        </SMCPage>
    );
}

const mapStateToProps = state => ({
    isUserAdmin: isUserAdminSelector(state),
    isAdminReadOnly: isAdminReadOnlySelector(state),
    isSystemUser: isSystemUserSelector(state),
    systemUserPermissions: systemUserPermissionsSelector(state)
});

const mapDispatchToProps = null;

export default connect(mapStateToProps, mapDispatchToProps)(SystemUserResults);